import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import jalaliday from 'jalali-plugin-dayjs';

export type Dayjs = dayjs.Dayjs;
export type ConfigType = dayjs.ConfigType;
export type QUnitType = dayjs.QUnitType;
export type OpUnitType = dayjs.OpUnitType;
export type ManipulateType = dayjs.ManipulateType;

dayjs.extend(jalaliday);
dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(minMax);
dayjs.extend(isBetween);

export const getUTCNow = () => dayjs().utc(true).valueOf();
export const getDate = (date?: ConfigType): Dayjs => dayjs(date);

export const dayjsToUnix = (
  dayjsV?: Dayjs | null,
  format: string = 'YYYY-MM-DD'
) => {
  if (!dayjsV) {
    dayjsV = getDate();
  }

  return new Date(dayjsV.format(format)).getTime();
};

export const isToday = (date: ConfigType) => {
  const chosenDate = dayjs(date).utc().date();
  const today = dayjs().utc().date();

  return chosenDate === today;
};

export type TDateFormat =
  | 'dddd، D MMMM - HH:mm'
  | 'dddd، D MMMM YYYY - HH:mm'
  | 'dddd، D MMMM'
  | 'dddd، D MMMM YYYY'
  | 'dddd D MMMM YYYY ساعت HH:mm'
  | 'D MMMM YYYY ساعت HH:mm'
  | 'D MMMM YYYY'
  | 'D MMMM'
  | 'D MMMM - HH:mm'
  | 'D MMMM YYYY - HH:mm'
  | 'YYYY'
  | 'MMMM'
  | 'YYYY/M/D'
  | 'YYYY/MM/DD'
  | 'HH:mm'
  | 'HH:mm:ss'
  | 'YYYY-MM-DD'
  | 'YYYY/MM/DD HH:mm'
  | 'YYYY/MM/DD HH:mm:ss';

export const toGregoryDate = (
  date: ConfigType,
  format: TDateFormat
): string => {
  // @ts-ignore
  return dayjs(date, { jalali: true }).format(format);
};

export const convertPersianToPersianDate = (
  date: ConfigType,
  format: TDateFormat
): string => {
  // @ts-ignore
  return dayjs(date, { jalali: true })
    .locale('fa')
    .calendar('jalali')
    .format(format);
};

export const toFormattedDate = (
  dateTime?: ConfigType,
  format: TDateFormat = 'dddd، D MMMM YYYY',
  utc: boolean = false
) => {
  return dayjs(dateTime)
    .utc(utc)
    .locale('fa')
    .calendar('jalali')
    .format(format);
};

// TODO: get second parameter as object
export const toTime = (
  dateTime?: ConfigType,
  full: boolean = true,
  utc: boolean = false
) => {
  return toFormattedDate(dateTime, full ? 'HH:mm:ss' : 'HH:mm', utc);
};

export const toPersianDateTime = (
  dateTime?: ConfigType,
  full: boolean = true,
  customFormat?: TDateFormat | null
) => {
  return toFormattedDate(
    dateTime,
    customFormat
      ? customFormat
      : full
        ? 'dddd D MMMM YYYY ساعت HH:mm'
        : 'D MMMM YYYY ساعت HH:mm'
  );
};

export const sleep = (ms: number) => {
  if (typeof ms !== 'number') {
    throw new Error('invalid millisecond. millisecond must be a number');
  }
  return new Promise((resolve) => {
    setTimeout(() => resolve(true), ms);
  });
};

export const isBetweenTheseDates = (
  current: Dayjs,
  start: ConfigType,
  end: ConfigType
): boolean => {
  return current.isBetween(start, end);
};

export const setTime = (
  hour: number,
  minute: number = 0,
  second: number = 0
): Dayjs => {
  return dayjs().set('hour', hour).set('minute', minute).set('second', second);
};

export const differenceBetweenTwoDate = (
  firstDate: Dayjs,
  secondDate?: ConfigType,
  unit?: QUnitType | OpUnitType,
  float?: boolean
): number => {
  return firstDate.diff(secondDate, unit, float);
};

export const getCurrentYear = (): number => {
  return dayjs().calendar('jalali').year();
};

export const getSubstractDate = (
  value: number,
  unit?: ManipulateType
): Dayjs => {
  return getDate().subtract(value, unit);
};

export const findMaxDate = (...dates: Dayjs[]): Dayjs | null => {
  return dayjs.max(dates);
};
